import {
    Dropdown,
    Option,
    OptionGroup,
    type OptionOnSelectData,
    type SelectionEvents,
} from '@fluentui/react-components';
import type { Column } from '@tanstack/react-table';
import { type Dispatch, type SetStateAction, useCallback } from 'react';
import { useSharedStyles } from '../../shared-styles.js';
import { typedSetStringColumnFilterValue } from '../filter/index.js';
import { STRING_FILTER_OPERATION, type StringFilterValuesWithoutInArray } from '../filter/operation.js';
import type { StringFilterSchemaWithoutInArray } from '../filter/schema.js';
import { DROPDOWN_STRING_OPTIONS } from './data.js';

type StringOperationProps<TData, TValue> = {
    column: Column<TData, TValue>;
    dropdownValue: StringFilterValuesWithoutInArray['id'];
    resetListFilter: (resetDropdown: boolean) => void;
    setDropdownValue: Dispatch<SetStateAction<StringFilterValuesWithoutInArray['id']>>;
};

export function StringOperation<TData, TValue>({
    column,
    dropdownValue,
    resetListFilter,
    setDropdownValue,
}: StringOperationProps<TData, TValue>) {
    const sharedStyles = useSharedStyles();

    const onDropdownChange = useCallback(
        (_: SelectionEvents, changeData: OptionOnSelectData) => {
            const operation = changeData.selectedOptions[0] as typeof dropdownValue;

            if (
                dropdownValue === (STRING_FILTER_OPERATION.inArray.id as StringFilterValuesWithoutInArray['id']) &&
                operation !== (STRING_FILTER_OPERATION.inArray.id as StringFilterValuesWithoutInArray['id'])
            ) {
                resetListFilter(false);
            }

            setDropdownValue(operation);

            typedSetStringColumnFilterValue(column, {
                ...(column.getFilterValue() as StringFilterSchemaWithoutInArray | undefined),
                operation,
            });
        },
        [column, dropdownValue, resetListFilter, setDropdownValue]
    );

    return (
        <Dropdown
            className={sharedStyles.dropdown}
            value={STRING_FILTER_OPERATION[dropdownValue].label}
            selectedOptions={[dropdownValue]}
            onOptionSelect={onDropdownChange}
            listbox={{
                className: sharedStyles.dropdownListbox,
            }}
        >
            {DROPDOWN_STRING_OPTIONS.map((optionGroup, index) => (
                <OptionGroup key={index}>
                    {optionGroup.map((option) => {
                        return (
                            <Option key={option} value={option} text={STRING_FILTER_OPERATION[option].label}>
                                {STRING_FILTER_OPERATION[option].label}
                            </Option>
                        );
                    })}
                </OptionGroup>
            ))}
        </Dropdown>
    );
}
